import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Table } from "reactstrap";

import { CustomModal } from "components/CustomModal";
import { CustomButton } from "components/CustomButton";
import {
  schoolAdminRequestsApi,
  approveSchoolAdminRequest,
  declineSchoolAdminRequest,
} from "../../api/api.service";

/**
 * People who registered a district themselves (from its public page), waiting
 * for us to decide. Shown at the top of District Hub so a registration is
 * reviewed where districts are managed, not on the school-administrator queue.
 *
 * Same records and the same approve / decline calls as School Admin Requests
 * (a registration is a PENDING owner membership there too), so the two screens
 * can never disagree. Approving makes them the district's owner.
 */
const DistrictRegistrations = ({ onDecided }) => {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [review, setReview] = useState(null); // { request, mode: "approve" | "decline" }
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await schoolAdminRequestsApi({ status: "PENDING", page: 1, limit: 100 });
      const all = Array.isArray(res?.data) ? res.data : [];
      setRows(all.filter((r) => r.districtRole));
    } catch (e) {
      setRows([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const close = () => {
    if (submitting) return;
    setReview(null);
    setNote("");
  };

  const submit = async () => {
    const text = note.trim();
    if (!review || !text) return;
    setSubmitting(true);
    try {
      if (review.mode === "approve") {
        await approveSchoolAdminRequest({ id: review.request.id, reviewNote: text });
        toast.success(`${review.request.userName} is now the owner of ${review.request.districtName}.`);
      } else {
        await declineSchoolAdminRequest({ id: review.request.id, declineReason: text });
        toast.success("Registration declined. They have been emailed the reason.");
      }
      setReview(null);
      setNote("");
      await load();
      if (onDecided) onDecided();
    } catch (e) {
      toast.error(e?.message || "Could not save that decision.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!loaded || rows.length === 0) return null;

  // Two people registering the same district: approve one only after comparing.
  const perDistrict = {};
  rows.forEach((r) => {
    perDistrict[r.districtId] = (perDistrict[r.districtId] || 0) + 1;
  });

  return (
    <div className="card mb-4" id="registrations">
      <div className="card-body">
        <h5 className="mb-1">
          Registrations waiting for your review <span className="cs-badge cs-badge-pending ms-1">{rows.length}</span>
        </h5>
        <p className="text-muted font-size-13 mb-3">
          People who registered a district from its public page. Approving makes them the district&rsquo;s owner:
          check they really speak for it first.
        </p>
        <div className="table-responsive">
          <Table borderless className="mb-0 align-middle">
            <thead>
              <tr>
                <th>District</th>
                <th>Registered by</th>
                <th style={{ width: 200 }}>Sent</th>
                <th style={{ width: 190 }} className="text-end">
                  <span className="visually-hidden">Decision</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div>{r.districtName || "—"}</div>
                    {perDistrict[r.districtId] > 1 ? (
                      <div className="text-danger font-size-12">
                        {perDistrict[r.districtId]} people registered this district. Compare them before approving
                        one.
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div>{r.userName || "—"}</div>
                    <div className="text-muted font-size-12">{r.userEmail}</div>
                    {r.userStatus !== "ACTIVE" ? (
                      <div className="text-danger font-size-12">
                        Account is {r.userStatus}: can&rsquo;t be approved until they confirm their email.
                      </div>
                    ) : null}
                  </td>
                  <td className="font-size-12 text-muted">{new Date(r.requestedAt).toLocaleString()}</td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <CustomButton
                        title="Approve"
                        color="primary"
                        className="btn-sm"
                        disabled={r.userStatus !== "ACTIVE"}
                        onClick={() => setReview({ request: r, mode: "approve" })}
                      />
                      <CustomButton
                        title="Decline"
                        color="light"
                        className="btn-sm"
                        onClick={() => setReview({ request: r, mode: "decline" })}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <CustomModal isOpen={!!review} onClose={close} toggle={close} size="md">
        {review && (
          <div className="px-3 pb-3">
            <h5 className="mb-2">
              {review.mode === "approve" ? "Approve" : "Decline"} {review.request.userName}
            </h5>
            <p className="text-muted font-size-13">
              {review.mode === "approve" ? (
                <>
                  They become the owner of <strong>{review.request.districtName}</strong>: they can add its schools,
                  invite administrators, decide which teachers join and publish its page. Record how you confirmed
                  they speak for the district.
                </>
              ) : (
                <>This reason is emailed to them word for word, so write it for them to read.</>
              )}
            </p>
            <textarea
              className="form-control mb-3"
              rows={3}
              autoFocus
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                review.mode === "approve"
                  ? "e.g. Called the district office; confirmed they are the superintendent."
                  : "e.g. We couldn't confirm you work for this district. Please register with your district email."
              }
            />
            <div className="d-flex justify-content-end gap-2">
              <CustomButton title="Cancel" color="light" onClick={close} disabled={submitting} />
              <CustomButton
                title={review.mode === "approve" ? "Approve" : "Decline"}
                color={review.mode === "approve" ? "primary" : "danger"}
                onClick={submit}
                disabled={!note.trim() || submitting}
                loading={submitting}
              />
            </div>
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default DistrictRegistrations;
